import { ChangeDetectionStrategy, Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { getIconColor } from '@core/attachment/helpers';
import { DataroomService } from '@core/dataroom/dataroom.service';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DataroomDataSource } from '../dataroom-data-source';
import { mainParentId } from '../dataroom-defaults';
import { UserDataroomService } from '@core/dataroom/user-dataroom.service';
import { ClientDataroomService } from '@core/dataroom/client-dataroom.service';
import { TypedFormGroup } from '@feature/forms/form-model';

@Component({
    selector: 'wai-dataroom-browser',
    templateUrl: './dataroom-browser.component.html',
    styleUrls: ['./dataroom-browser.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataroomBrowserComponent implements OnInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  public activeNodeChange: Observable<DataroomNode>;

  public dataSource: DataroomDataSource;

  /** Disabled nodes IDs, folders are always enabled so navigation works as expected */
  @Input() public disabledNodeIds: DataroomNode['id'][];

  /** Hide nodes with specified IDs, including folders */
  @Input() public hiddenNodeIds: DataroomNode['id'][];

  serviceUsed: DataroomService | UserDataroomService | ClientDataroomService;
  @Input('service') set setService(service: DataroomService | UserDataroomService | ClientDataroomService) {
    this.serviceUsed = service;
  }

  public nodes: Observable<DataroomNode[]>;

  public openedNodeChange: Observable<DataroomNode>;

  /** Allow to select nodes of specified type only */
  @Input() public selectableNodeTypes: DataroomNode['type'][] = ['file', 'folder'];

  public selectedNodeChange: Observable<DataroomNode>;

  public visuallySelectedNode: DataroomNode;

  private activeNodeSubject: BehaviorSubject<DataroomNode>;

  private openedNodeSubject: BehaviorSubject<DataroomNode>;

  private selectedNodeSubject: BehaviorSubject<DataroomNode>;

  public get activeNode(): DataroomNode {
    return this.activeNodeSubject.getValue();
  }

  public get icon() {
    return { file: faFile };
  }

  public get selectedNode(): DataroomNode {
    return this.selectedNodeSubject.getValue();
  }

  /** Move/upload folder pickers list children only — always surface dataroom root as first row. */
  public get showsRootDestinationRow(): boolean {
    return this.selectableNodeTypes?.includes('folder');
  }

  private get emptyNode(): DataroomNode {
    return DataroomNode.create({ id: mainParentId });
  }

  constructor(
    @Optional() @Inject(DataroomService) private dataroomService: DataroomService,
    @Optional() @Inject(UserDataroomService) private userDataroomService: UserDataroomService,
    @Optional() @Inject(ClientDataroomService) private clientDataroomService: ClientDataroomService,
  ) {
    this.serviceUsed = this.dataroomService || this.userDataroomService || this.clientDataroomService;
    this.activeNodeSubject = new BehaviorSubject(this.emptyNode);
    this.activeNodeChange = this.activeNodeSubject.asObservable();

    this.selectedNodeSubject = new BehaviorSubject(this.emptyNode);
    this.selectedNodeChange = this.selectedNodeSubject.asObservable();

    this.openedNodeSubject = new BehaviorSubject(this.emptyNode);
    this.openedNodeChange = this.openedNodeSubject.asObservable();
  }

  public getIconColor(node: DataroomNode): string {
    return node.type === 'folder' ? (this.selectedNode.id === node.id ? 'mat-primary' : 'mat-secondary') : getIconColor(node.file?.type);
  }

  public goBack(): void {
    let node: DataroomNode;

    if (this.activeNode.ancestors) {
      // Backend does not provide full DataroomNode but only partial of it, i.e. DataroomNodeChoice. Add manually
      // 'folder' type so setting active node works on navigating back.
      node = DataroomNode.create({ ...this.activeNode.ancestors[0], type: 'folder' });
    } else {
      node = this.emptyNode;
    }

    this.visuallySelectedNode = this.emptyNode;
    this.selectNode(node);
    this.goTo(node);
  }

  public goTo(node: DataroomNode): void {
    if (node?.id === null || node?.id === mainParentId) {
      return this.loadInitialList();
    }

    this.activeNodeSubject.next(node);
    this.selectNode(node);
    this.dataSource.load(null, node.id);
  }

  public isDisabled(node: DataroomNode): boolean {
    // folders are never disabled so user can navigate through them
    if (node.type === 'folder') {
      return false;
    }

    if (this.disabledNodeIds?.includes(node.id)) {
      return true;
    }

    return !this.selectableNodeTypes.includes(node.type);
  }

  public isFolder(node: DataroomNode): boolean {
    return node.type === 'folder';
  }

  public ngOnInit(): void {
    this.dataSource = new DataroomDataSource(this.serviceUsed);

    if (this.hiddenNodeIds) {
      this.nodes = this.dataSource.connect().pipe(map((nodes) => nodes.filter((node) => !this.hiddenNodeIds.includes(node.id))));
    } else {
      this.nodes = this.dataSource.connect();
    }

    this.loadInitialList();
  }

  public onOpen(node: DataroomNode): void {
    if (node.type === 'folder') {
      this.visuallySelectedNode = this.emptyNode;
      this.goTo(node);
    }

    if (node.type === 'file') {
      this.selectedNodeSubject.next(node);
      this.openedNodeSubject.next(node);
    }
  }

  public onSelect(node: DataroomNode): void {
    // folders are always visually selectable
    if (node.type === 'folder' || this.selectableNodeTypes.includes(node.type)) {
      this.visuallySelectedNode = node;
    }

    this.selectNode(node);
  }

  /** Root listing is not a child row — expose dataroom root as an explicit move/upload destination. */
  public selectRootFolderDestination(): void {
    const rootFolder = DataroomNode.create({ id: mainParentId, type: 'folder' });
    this.visuallySelectedNode = rootFolder;
    this.selectNode(rootFolder);
  }

  public isRootFolderDestinationSelected(): boolean {
    return this.showsRootDestinationRow && this.selectedNode.id === mainParentId;
  }

  private loadInitialList(): void {
    this.activeNodeSubject.next(this.emptyNode);
    this.dataSource.load();
  }

  private selectNode(node: DataroomNode): void {
    if (this.selectableNodeTypes.includes(node.type)) {
      this.selectedNodeSubject.next(DataroomNode.create({ ...node }));
    } else {
      this.selectedNodeSubject.next(this.emptyNode);
    }
  }
}
